<template>
  <div class="materials">
    <div class="header-row">
      <h3>Materials</h3>
      <span class="meta-count">{{ (materials || []).length }} item(s)</span>
    </div>

    <table class="material-table">
      <thead>
      <tr>
        <th>Name</th>
        <th>Batch</th>
        <th v-if="assType === 'experiment'">Relation</th>
        <th>Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="m in materials" :key="m.edge_id ?? m.id" @click="navigateToMaterialDetail(m.id)" class="material-row" >
        <td>{{ m.name }}</td>
        <td>{{ m.batch }}</td>
        <td v-if="assType === 'experiment'">{{ m.relation_type }}</td>
        <td>
          <button class="remove-btn" @click.stop="removeMaterial(m.id, id, m)">
            <i class="bi bi-trash"></i>
          </button>
        </td>
      </tr>
      <tr v-if="!materials || materials.length === 0">
        <td :colspan="assType === 'experiment' ? 4 : 3" class="empty">No materials in this entity yet.</td>
      </tr>
      </tbody>
    </table>

    <div class="add-material-controls">
      <select v-model="selectedMaterialId">
        <option disabled value="">Select a material…</option>
        <option
            v-for="m in availableMaterials"
            :value="m.id"
            :key="m.id"
            :title="fullMaterialOptionLabel(m)"
        >
          {{ materialOptionLabel(m) }}
        </option>
      </select>
      <select
        v-if="assType === 'experiment'"
        v-model="selectedRelationTypeId"
        aria-label="Relation type"
      >
        <option disabled value="">Relation…</option>
        <option v-for="rt in relationTypes" :key="rt.id" :value="rt.id">{{ rt.name }}</option>
      </select>
      <button type="button" @click="addMaterial" :disabled="!selectedMaterialId || (assType === 'experiment' && !selectedRelationTypeId)">
        Add Material
      </button>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import axios from "axios";
import { API_ENDPOINTS } from "@/services/config";
import router from "@/router";
import { mapState } from "vuex";
import { createEdge, deleteEdge } from "@/utils/graphEdges";

export default {
  name: "ProjectMaterials",
  props: {
    materials: {
      type: Array,
      required: true,
    },
    id: {
      type: Number,
      required: true,
    },
    assType: {
      type: String,
      required: true
    },
    projId: {
      type: Number
    },
    // Parent should pass a function that refetches project materials
    refreshMaterials: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      availableMaterials: [],
      selectedMaterialId: "",
      relationTypes: [],
      selectedRelationTypeId: "",
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.fetchAvailableMaterials();
    if (this.assType === "experiment") this.fetchRelationTypes();
  },
  watch: {
    // When the project's materials change, recompute available list
    materials: {
      handler() {
        this.fetchAvailableMaterials();
      },
      deep: true,
    },
  },
  methods: {
    async fetchRelationTypes() {
      try {
        const res = await axios.get(API_ENDPOINTS.experimentFilterOptions, {
          params: { entra_id: this.loggedInUser?.entra_id },
        });
        this.relationTypes = res.data?.material_relation_types || [];
        const preferred = this.relationTypes.find((rt) => rt.name === "uses");
        this.selectedRelationTypeId = (preferred ?? this.relationTypes[0])?.id ?? "";
      } catch (error) {
        this.toast.error("Failed to fetch material relation types.");
        console.error("Error fetching relation types:", error);
      }
    },
    fullMaterialOptionLabel(material) {
      return `${material?.name ?? ""} — ${material?.batch ?? ""}`;
    },
    materialOptionLabel(material) {
      const label = this.fullMaterialOptionLabel(material);
      const maxLength = 80;
      if (label.length <= maxLength) {
        return label;
      }
      return `${label.slice(0, maxLength - 1)}…`;
    },
    async fetchAvailableMaterials() {
      try {
        let res
        if (this.assType == "workflow") {
           res = await axios.get(API_ENDPOINTS.materialProject, {
          params: {
            project_id: this.projId,
          }
        });
        }
        else {
           res = await axios.get(API_ENDPOINTS.materials);
        }
        const attachedIds = new Set((this.materials || []).map(m => m.id));
        this.availableMaterials = (res.data || []).filter(m => !attachedIds.has(m.id));
      } catch (error) {
        if (error?.response?.status !== 404) {
          this.toast.error("Failed to fetch available materials.");
          console.error("Error fetching materials:", error);
        }
        this.availableMaterials = [];
      }
    },
    async addMaterial() {
      try {
        if (this.assType == "project") {
          await axios.post(API_ENDPOINTS.materialProject, {
          project_id: this.id,
          material_id: Number(this.selectedMaterialId),
        });
        }
        else if (this.assType == "workflow") {
          await axios.post(API_ENDPOINTS.materialWorkflow, {
          workflow_id: this.id,
          material_id: Number(this.selectedMaterialId),
        });
        }
        else if (this.assType == "experiment") {
          await createEdge({
            srcNodeId: this.id,
            dstNodeId: Number(this.selectedMaterialId),
            relationTypeId: Number(this.selectedRelationTypeId),
            entraId: this.loggedInUser?.entra_id,
          });
        }
        this.toast.success("Material added successfully.");
        this.selectedMaterialId = "";
        await this.refreshMaterials();
        this.fetchAvailableMaterials();
      } catch (error) {
        this.toast.error("Failed to add material.");
        console.error("Error adding material:", error);
      }
    },
    async removeMaterial(materialId, id, material) {
      try {
        if (this.assType == "experiment") {
          await deleteEdge(material.edge_id, this.loggedInUser?.entra_id);
        }
        else if (this.assType == "project") {
          await axios.delete(API_ENDPOINTS.materialProject, {
          params: {
            project_id: id,
            material_id: materialId,
          },
        });
        }

        else if (this.assType == "workflow") {
          console.log("HEJEHH ", id, materialId)
          await axios.delete(API_ENDPOINTS.materialWorkflow, {
          params: {
            workflow_id: id,
            material_id: materialId,
          },
        });
        }
        
        await this.refreshMaterials();
        this.fetchAvailableMaterials();
        this.toast.success("Material removed successfully.");
      } catch (error) {
        this.toast.error("Failed to remove material.");
        console.error("Error removing material:", error);
      }
    },
    navigateToMaterialDetail(materialId) {
      router.push(`/materials/${materialId}`);
    }
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.materials {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  .header-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 20px;
    padding-right: 58px;
  }

  h3 {
    margin: 0;
    color: $primary-color;
  }

  .meta-count {
    font-size: 0.92rem;
    font-weight: 500;
    color: rgba($primary-color, 0.8);
  }

  .material-table {
    width: 100%;
    margin-bottom: 20px;

    tr:hover {
      background-color: $form-color;
    }

    .remove-btn {
      background: none;
      border: none;
      cursor: pointer;

      i {
        color: $primary-color;
        font-size: 1.2rem;

        &:hover {
          color: darken($primary-color, 10%);
        }
      }
    }

    .empty {
      text-align: center;
      color: #666;
      font-style: italic;
    }
  }

  .material-row {
    cursor: pointer;
  }

  .add-material-controls {
    display: flex;
    gap: 10px;
    min-width: 0;

    select {
      flex: 1;
      min-width: 0;
      width: 100%;
      padding: 5px;
      border-radius: 4px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    button {
      background-color: $primary-color;
      color: #fff;
      padding: 5px 10px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
  }
}
</style>
